import styled from "@emotion/styled";
import { getHexToRgba } from "@nykaa/payment-ui/src/common/helpers/getHexToRgba";


export const CouponCardWrapper = styled.div<{ isDisabled?: boolean, theme: Theme }>`
  width: 100%;
  max-width: 288px;
  background: ${({ isDisabled, theme }) => (isDisabled
    ? theme?.colors?.couponCardDisabledBg
    : theme?.colors?.couponCardBg
  )};
  border-radius: ${({ theme }) => theme?.borders?.radius35};
  overflow: hidden;
  pointer-events: ${({ isDisabled }) => (isDisabled ? "none" : "auto")};
  position: relative;
`;

export const TopSection = styled.div<{ theme: Theme }>`
  display: flex;
  align-items: flex-start;
  padding: 12px 75px 12px 12px;
  border: 1px solid ${({ theme }) => theme?.colors?.couponCardBorderColor};
  border-radius: ${({ theme }) => theme?.borders?.radius35} ${({ theme }) => theme?.borders?.radius35} 0 0;
  border-bottom: none;
  position: relative;
`;

export const BgIllustrationWrapper = styled.img`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
`;

export const OverlapIllustrationWrapper = styled.img`
  position: absolute;
  right: 0;
  bottom: 0;
  z-index: 1;
`;

export const LogoContainer = styled.div`
  width: 40px;
  height: 40px;
  background: ${({ theme }: { theme: Theme }) => theme?.colors?.white};
  border-radius: ${({ theme }) => theme?.borders?.radius30};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  img {
    max-width: 32px;
    max-height: 32px;
    object-fit: contain;
  }
`;

export const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-left: 8px;
  flex: 1;
  min-width: 0;
`;

export const SavingsText = styled.p`
  ${({ theme }) => theme?.typography?.type142};
  color: ${({ theme }: { theme: Theme }) => theme?.colors?.positive};
  margin: 0;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

export const DescriptionText = styled.p`
  ${({ theme }) => theme?.typography?.type121};
  color: ${({ theme }: { theme: Theme }) => theme?.colors?.textSecondary};
  margin: 0;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

/** Dashed divider with ticket punch holes (holes use page color — default white). */
export const DividerRow = styled.div<{ holeColor?: string, theme: Theme }>`
  position: relative;
  height: 1px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  &::before,
  &::after {
    content: "";
    position: absolute;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: ${({ holeColor, theme }) => holeColor || theme?.colors?.white};
    z-index: 2;
    border: 1px solid ${({ theme }) => theme?.colors?.couponCardBorderColor};
  }

  &::before {
    left: 0;
    transform: translateX(-50%);
  }

  &::after {
    right: 0;
    transform: translateX(50%);
  }
`;

export const DividerLine = styled.div<{ theme: Theme }>`
  flex: 1;
  height: 1px;
  border-top: 1px dashed ${({ theme }) => theme?.colors?.couponCardDividerLineColor};
`;

export const BottomRow = styled.div<{ theme: Theme }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid ${({ theme }) => theme?.colors?.couponCardBorderColor};
  border-top: none;
  border-radius: 0 0 ${({ theme }) => theme?.borders?.radius35} ${({ theme }) => theme?.borders?.radius35};
  position: relative;
  z-index: 1;
`;

export const CouponCodeContainer = styled.div<{ theme: Theme }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 6px;
  background: ${({ theme }) => theme?.colors?.white};
  border: 0.5px dashed ${({ theme }) => getHexToRgba(theme?.colors?.primary, 0.16)};
  border-radius: 4px;
  position: relative;
  max-width: 150px;

  &::before,
  &::after {
    content: "";
    position: absolute;
    width: 6px;
    height: 6px;
    top: 50%;
    transform: translateY(-50%);
    border-radius: 50%;
    z-index: 1;
  }

  &::before {
    left: -3px;
    background: ${({ theme }) => theme?.colors?.couponCodeNotchBgLeft};
    border-right: 0.5px dashed ${({ theme }) => getHexToRgba(theme?.colors?.primary, 0.16)};
  }

  &::after {
    right: -3px;
    background: ${({ theme }) => theme?.colors?.couponCodeNotchBgRight};
    border-left: 0.5px dashed ${({ theme }) => getHexToRgba(theme?.colors?.primary, 0.16)};
  }
`;

export const CouponCodeText = styled.span<{ theme: Theme }>`
  ${({ theme }) => theme?.typography?.type102};
  color: ${({ theme }) => theme?.colors?.textSecondary};
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

export const ApplyButton = styled.button<{ isApplied?: boolean, theme: Theme }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  flex-shrink: 0;
  background: ${({ theme }) => theme?.colors?.white};
  border: none;
  border-radius: 90px;
  cursor: pointer;
  min-width: 83px;
`;

export const ApplyButtonText = styled.span<{ theme: Theme }>`
  ${({ theme }) => theme?.typography?.type122};
  color: ${({ theme }) => theme?.colors?.primary};
`;
